import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { publicStorageUrl } from "@/lib/storage-url";
import type { Database } from "@/integrations/supabase/types";
import { Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { confirmDialog } from "@/lib/confirm-dialog";
import { AdminUploadButton } from "@/components/admin/AdminUploadButton";


type ClientLogo = Database["public"]["Tables"]["client_logos"]["Row"];
type ClientLogoInsert = Database["public"]["Tables"]["client_logos"]["Insert"];
type Category = Database["public"]["Enums"]["testimonial_category"];

const CATEGORY_LABELS: Record<Category, string> = {
  buergermeisterwahl: "Bürgermeisterwahlen",
  direktkandidaten: "Direktkandidaten",
  parteikampagnen: "Parteikampagnen",
  personalratswahl: "Personalratswahlen",
  "politische-kommunikation": "Politische Kommunikation",
  verbandskommunikation: "Verbandskommunikation",
  krisenkommunikation: "Krisenkommunikation",
  gemeindekommunikation: "Gemeindekommunikation",
};

const ALL_CATEGORIES = Object.keys(CATEGORY_LABELS) as Category[];

export const Route = createFileRoute("/_admin/admin/client-logos")({
  component: AdminClientLogosPage,
});

function AdminClientLogosPage() {
  const [items, setItems] = useState<ClientLogo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ClientLogo | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("client_logos")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) setError(error.message);
    else setItems(data ?? []);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function onDelete(item: ClientLogo) {
    if (
      !(await confirmDialog({
        title: `Logo „${item.name}" löschen?`,
        description: "Das Logo verschwindet aus der Laufleiste der Startseite.",
      }))
    )
      return;
    const { error } = await supabase.from("client_logos").delete().eq("id", item.id);
    if (error) {
      alert(error.message);
      return;
    }
    if (item.logo_path) {
      await supabase.storage.from("client-logos").remove([item.logo_path]);
    }
    load();
  }

  return (
    <div>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">Kundenlogos</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Logos für die Marquee-Sektion der Startseite. Veröffentlichte Logos laufen
            sortiert durch das Karussell.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setCreating(true);
          }}
          className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground"
        >
          <Plus className="h-4 w-4" /> Neu
        </button>
      </header>

      {error && (
        <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden …
          </div>
        ) : items.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Noch keine Logos angelegt.</p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((l) => (
              <li key={l.id} className="flex items-center gap-4 p-4">
                <div className="flex h-12 w-24 shrink-0 items-center justify-center rounded bg-secondary p-1">
                  {l.logo_path ? (
                    <img
                      src={publicStorageUrl("client-logos", l.logo_path) ?? ""}
                      alt={l.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : null}
                </div>
                <div className="flex-1">
                  <p className="font-display text-sm font-extrabold">{l.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Reihenfolge: {l.sort_order}</span>
                    <span
                      className={
                        "rounded px-2 py-0.5 " +
                        (l.published
                          ? "bg-brand-wahlen/20 text-foreground"
                          : "bg-muted text-muted-foreground")
                      }
                    >
                      {l.published ? "Veröffentlicht" : "Entwurf"}
                    </span>
                    {(l.categories ?? []).map((c) => (
                      <span
                        key={c}
                        className="rounded bg-secondary px-2 py-0.5 text-muted-foreground"
                      >
                        {CATEGORY_LABELS[c as Category] ?? c}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => {
                      setCreating(false);
                      setEditing(l);
                    }}
                    className="rounded border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary"
                  >
                    Bearbeiten
                  </button>
                  <button
                    onClick={() => onDelete(l)}
                    aria-label="Löschen"
                    className="rounded border border-destructive/40 px-2 py-1 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(editing || creating) && (
        <LogoForm
          initial={
            editing ?? { name: "", logo_path: "", sort_order: 0, published: false, categories: [] }
          }
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function LogoForm({
  initial,
  onClose,
  onSaved,
}: {
  initial: ClientLogo | ClientLogoInsert;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = "id" in initial;
  const [draft, setDraft] = useState<ClientLogoInsert>({ ...initial });
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      let logo_path = draft.logo_path ?? "";
      if (file) {
        const ext = file.name.split(".").pop() ?? "svg";
        const path = `${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("client-logos")
          .upload(path, file, { upsert: false, contentType: file.type });
        if (upErr) throw upErr;
        if (logo_path) {
          await supabase.storage.from("client-logos").remove([logo_path]);
        }
        logo_path = path;
      }

      if (!logo_path) {
        throw new Error("Bitte eine Logo-Datei hochladen.");
      }

      const payload: ClientLogoInsert = { ...draft, logo_path };

      if (isEdit && "id" in initial) {
        const { error } = await supabase
          .from("client_logos")
          .update(payload)
          .eq("id", (initial as ClientLogo).id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("client_logos").insert(payload);
        if (error) throw error;
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 md:p-10">
      <div className="w-full max-w-xl rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold">
            {isEdit ? "Logo bearbeiten" : "Neues Logo"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="rounded p-1 text-muted-foreground hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <Field label="Name (Anzeigetext / Alt)">
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
            />
          </Field>
          <Field label="Kategorien (mehrere möglich)">
            <div className="flex flex-wrap gap-3">
              {ALL_CATEGORIES.map((c) => {
                const checked = (draft.categories ?? []).includes(c);
                return (
                  <label
                    key={c}
                    className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const current = draft.categories ?? [];
                        const next = e.target.checked
                          ? Array.from(new Set([...current, c]))
                          : current.filter((x) => x !== c);
                        setDraft({ ...draft, categories: next });
                      }}
                    />
                    {CATEGORY_LABELS[c]}
                  </label>
                );
              })}
            </div>
          </Field>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Reihenfolge">
              <input
                type="number"
                value={draft.sort_order ?? 0}
                onChange={(e) =>
                  setDraft({ ...draft, sort_order: Number(e.target.value) || 0 })
                }
                className="w-full rounded-md border border-input bg-background p-2 text-sm"
              />
            </Field>
            <Field label="Veröffentlicht">
              <label className="flex h-9 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.published ?? false}
                  onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                />
                Sichtbar in Marquee
              </label>
            </Field>
          </div>
          <Field label="Logo-Datei (SVG/PNG)">
            <AdminUploadButton
              label={file || draft.logo_path ? "Logo ersetzen" : "Logo hochladen"}
              accept="image/*,image/svg+xml"
              currentName={file?.name ?? draft.logo_path ?? null}
              hint="SVG bevorzugt, sonst PNG mit transparentem Hintergrund."
              onFiles={(files) => setFile(files[0] ?? null)}
            />
          </Field>

        </div>

        {err && (
          <p className="mt-4 rounded border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            {err}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold"
          >
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
